import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./HeatmapPanel.css";
import useTopSuburbsByIndicator from "../../../hooks/useTopSuburbsByIndicator";

/** Tabs/indicadores */
const TABS = [
  { key: "rental_yield_pct",           label: "Median rental yield" },
  { key: "lt_growth_rate_20y_pct",     label: "LT growth rate" },
  { key: "median_price_change_5y_pct", label: "5 Years Median Change" },
  { key: "vacancy_rate_sqm",           label: "Vacancy rate" },
];

/** true => mayor es mejor; false => menor es mejor */
const IS_HIGHER_BETTER = {
  rental_yield_pct: true,
  lt_growth_rate_20y_pct: true,
  median_price_change_5y_pct: true,
  vacancy_rate_sqm: false,
};

const INVERTED_KEYS = new Set(["vacancy_rate_sqm"]);

function fmtPct(v) {
  if (typeof v !== "number" || !isFinite(v)) return "–";
  return new Intl.NumberFormat("es-CO", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v / 100);
}

function colorFor(value, min, max, invert = false) {
  if (!isFinite(value) || !isFinite(min) || !isFinite(max) || max === min) {
    return "#cfd6e4";
  }
  let t = (value - min) / (max - min);
  t = Math.max(0, Math.min(1, t));
  if (invert) t = 1 - t;
  const hue = 0 + (120 - 0) * t;
  return `hsl(${hue}, 70%, 45%)`;
}

const memCache = new Map();
const loadFromLS = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || "null"); } catch { return null; }
};
const saveToLS = (k, v) => {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch {}
};

async function geocodeCity(city) {
  const key = `CITY|${city}`;
  const lsKey = `LS_${key}`;
  if (memCache.has(key)) return memCache.get(key);
  const cached = loadFromLS(lsKey);
  if (cached) { memCache.set(key, cached); return cached; }

  const q = `${city}, Australia`;
  const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(q)}&limit=1`;

  let out = null;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const arr = await res.json();
    const hit = Array.isArray(arr) && arr[0] ? arr[0] : null;
    if (hit) {
      const bb = hit.boundingbox;
      const center =
        hit.lat && hit.lon ? [Number(hit.lat), Number(hit.lon)] : null;
      const bounds =
        bb?.length === 4
          ? [
              [Number(bb[0]), Number(bb[2])],
              [Number(bb[1]), Number(bb[3])],
            ]
          : null;
      out = { bounds, center };
    }
  } catch (err) {
    console.warn("geocodeCity error:", q, err);
  }

  memCache.set(key, out);
  saveToLS(lsKey, out);
  return out;
}

async function fetchLocalCityGeoJSON(city) {
  const fname = city.trim().toLowerCase().replace(/\s+/g, "-");
  const url = `/geo/${fname}.geojson`;
  try {
    const res = await fetch(url, { cache: "force-cache" });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

const normalize = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

function getFeatureName(props) {
  const p = props || {};
  return p.name ?? p.SAL_NAME21 ?? p.sal_name21 ?? p["tags"]?.name ?? null;
}

/* ✅ AQUÍ VIENE LA CORRECCIÓN IMPORTANTE:
   Envolver TODO el HeatmapPanel en un contenedor capturable */
export default function HeatmapPanel({ city }) {
  return (
    <div className="heatmap-container">
      <HeatmapPanelContent city={city} />
    </div>
  );
}

/* ======= COMPONENTE ORIGINAL SIN MODIFICAR ======= */
function HeatmapPanelContent({ city }) {
  const [active, setActive] = useState(TABS[0].key);

  const higherIsBetter = IS_HIGHER_BETTER[active] ?? true;
  const invert = INVERTED_KEYS.has(active) ? true : !higherIsBetter;

  const { data: dataAll = [] } =
    useTopSuburbsByIndicator(city, active, { order: "desc", limit: 5000 }) ?? {};

  const rows = useMemo(
    () =>
      (Array.isArray(dataAll) ? dataAll : [])
        .filter((d) => d && typeof d.suburb === "string" && isFinite(Number(d.value)))
        .map((d) => ({ suburb: d.suburb, city: d.city ?? city, value: Number(d.value) })),
    [dataAll, city]
  );

  const top10 = useMemo(() => {
    const sorted = [...rows].sort((a, b) =>
      higherIsBetter ? b.value - a.value : a.value - b.value
    );
    return sorted.slice(0, 10);
  }, [rows, higherIsBetter]);

  const allValues = useMemo(
    () => rows.map((d) => d.value).filter((v) => isFinite(v)),
    [rows]
  );
  const scaleMin = useMemo(() => (allValues.length ? Math.min(...allValues) : NaN), [allValues]);
  const scaleMax = useMemo(() => (allValues.length ? Math.max(...allValues) : NaN), [allValues]);

  const mapRef = useRef(null);
  const mapDivRef = useRef(null);
  const polysLayerRef = useRef(null);
  const [, setUsingLocalGeo] = useState(false);
  const layerByNameRef = useRef(new Map());

  useEffect(() => {
    if (mapRef.current || !mapDivRef.current) return;
    const canvasRenderer = L.canvas({ padding: 0.5, tolerance: 5 });

    mapRef.current = L.map(mapDivRef.current, {
      zoomControl: true,
      attributionControl: true,
      preferCanvas: true,
    });
    mapRef.current.__canvasRenderer = canvasRenderer;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
    }).addTo(mapRef.current);

    polysLayerRef.current = L.layerGroup().addTo(mapRef.current);
    mapRef.current.setView([-25.27, 133.77], 4);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!mapRef.current) return;
      polysLayerRef.current?.clearLayers();
      layerByNameRef.current = new Map();

      const localGJ = await fetchLocalCityGeoJSON(city);
      if (cancelled) return;

      if (localGJ) {
        setUsingLocalGeo(true);

        const valByName = new Map();
        for (const r of rows) valByName.set(normalize(r.suburb), r.value);

        const styleFn = (feat) => {
          const nm = getFeatureName(feat?.properties);
          const v = valByName.get(normalize(nm));
          const fill = colorFor(v, scaleMin, scaleMax, invert);
          return {
            renderer: mapRef.current.__canvasRenderer,
            color: "#2c2c2c",
            weight: 0.8,
            fillColor: fill,
            fillOpacity: 0.55,
          };
        };

        const onEach = (feat, layer) => {
          const nm = getFeatureName(feat?.properties) || "Suburb";
          const v = valByName.get(normalize(nm));
          layerByNameRef.current.set(normalize(nm), layer);
          layer.bindTooltip(
            `<strong>${nm}</strong>${isFinite(v) ? `<br/>${fmtPct(v)}` : ""}`,
            { direction: "top", sticky: true }
          );
        };

        L.geoJSON(localGJ, {
          style: styleFn,
          onEachFeature: onEach,
          filter: (feat) => {
            const nm = getFeatureName(feat?.properties);
            return valByName.has(normalize(nm));
          },
        }).addTo(polysLayerRef.current);

        try {
          const b = L.geoJSON(localGJ).getBounds?.();
          if (b && b.isValid()) mapRef.current.fitBounds(b.pad(0.05));
        } catch {}
        return;
      }

      setUsingLocalGeo(false);
      const geo = await geocodeCity(city);
      if (!geo) return;
      if (geo.bounds) mapRef.current.fitBounds(geo.bounds, { padding: [30, 30] });
      else if (geo.center) mapRef.current.setView(geo.center, 11);
    })();

    return () => { cancelled = true; };
  }, [city, rows, scaleMin, scaleMax, invert]);

  const focusSuburb = (name) => {
    const lyr = layerByNameRef.current.get(normalize(name));
    if (!lyr || !mapRef.current) return;
    try {
      const b = lyr.getBounds?.();
      if (b && b.isValid()) {
        mapRef.current.fitBounds(b.pad(0.15));
        lyr.openTooltip?.();
      }
    } catch {}
  };

  const legendMin = isFinite(scaleMin) ? fmtPct(scaleMin) : "–";
  const legendMax = isFinite(scaleMax) ? fmtPct(scaleMax) : "–";
  const gradientStyle = INVERTED_KEYS.has(active)
    ? { background: "linear-gradient(90deg,#62c66e 0%, #82d37e 20%, #b7d57e 40%, #e9c56c 60%, #ee9a57 80%, #e1524b 100%)" }
    : { background: "linear-gradient(90deg,#e1524b 0%, #ee9a57 20%, #e9c56c 40%, #b7d57e 60%, #82d37e 80%, #62c66e 100%)" };

  return (
    <div className="heatmap-card" role="region" aria-label="Heatmap by indicator">
      <div className="heatmap-head">
        <div className="heatmap-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`heatmap-tab${active === t.key ? " active" : ""}`}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="heatmap-body">
        <div className="heatmap-mapwrap">
          <div id="heatmap-map" ref={mapDivRef} />
        </div>

        <aside className="heatmap-side">
          <div style={{ margin: "4px 4px 10px 4px" }} aria-hidden="true">
            <div className="heatmap-gradient" style={gradientStyle} />
            <div className="heatmap-legend-scale" style={{ marginTop: 4 }}>
              <span>{legendMin}</span>
              <span>{legendMax}</span>
            </div>
          </div>

          <h4>Top 10</h4>
          {top10.map((row, i) => {
            const frac =
              isFinite(scaleMin) &&
              isFinite(scaleMax) &&
              scaleMax !== scaleMin
                ? (row.value - scaleMin) / (scaleMax - scaleMin)
                : 0.5;

            const adj = Math.max(0, Math.min(1, INVERTED_KEYS.has(active) ? 1 - frac : frac));
            const barColor = colorFor(row.value, scaleMin, scaleMax, INVERTED_KEYS.has(active));

            return (
              <div
                key={`${row.suburb}-${i}`}
                className="hm-row hm-row-clickable"
                onClick={() => focusSuburb(row.suburb)}
              >
                <div className="hm-name">{row.suburb}</div>
                <div className="hm-val">{fmtPct(row.value)}</div>
                <div className="hm-bar">
                  <span
                    style={{
                      width: `${Math.max(4, Math.min(100, adj * 100))}%`,
                      background: barColor,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </aside>
      </div>
    </div>
  );
}
